import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { StatCard } from '@/components/StatCard';
import { Tabs, Tab } from '@/components/Tabs';
import { DataTable, Column } from '@/components/DataTable';
import { BookOpen, Package, Tag, ArrowRight } from 'lucide-react';

// Mock data types
interface MarketplaceProduct {
  id: string;
  product: string;
  quantity: number;
  pricePerUnit: number;
  total: number;
  type: 'offer' | 'request' | 'listing';
}

interface Negotiation {
  id: string;
  product: string;
  quantity: number;
  price: number;
  total: number;
  type: 'offer' | 'request';
}

// Mock data for marketplace preview
const mockMarketplaceData: MarketplaceProduct[] = [
  { id: '1', product: 'Brent Crude FOB', quantity: 100, pricePerUnit: 85.50, total: 8550, type: 'offer' },
  { id: '2', product: 'Diesel Grade A', quantity: 50, pricePerUnit: 92.00, total: 4600, type: 'offer' },
  { id: '3', product: 'WTI Crude', quantity: 200, pricePerUnit: 84.00, total: 16800, type: 'listing' },
  { id: '4', product: 'Gasoline Premium', quantity: 75, pricePerUnit: 88.50, total: 6637.50, type: 'request' },
  { id: '5', product: 'Heating Oil', quantity: 120, pricePerUnit: 90.25, total: 10830, type: 'offer' },
];

// Mock data for negotiations preview
const mockNegotiationsData: Negotiation[] = [
  { id: '1', product: 'WTI Crude', quantity: 200, price: 84.00, total: 16800, type: 'offer' },
  { id: '2', product: 'Brent Crude FOB', quantity: 100, price: 85.50, total: 8550, type: 'request' },
  { id: '3', product: 'Diesel Grade A', quantity: 50, price: 92.00, total: 4600, type: 'offer' },
  { id: '4', product: 'Gasoline Premium', quantity: 75, price: 88.50, total: 6637.50, type: 'request' },
];

/**
 * DashboardPage component - Main dashboard landing page after authentication
 * 
 * Features:
 * - Welcome message with user name
 * - Tutorial cards section (Learn Basics, Products, Offers)
 * - Marketplace preview block with tabs and table
 * - Negotiations preview block with tabs and table
 * - Modern industrial trading aesthetic background
 */
export const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Marketplace preview state
  const [marketplaceTab, setMarketplaceTab] = useState<string>('all');
  const marketplaceTabs: Tab[] = [
    { id: 'all', label: 'All listings' },
    { id: 'offers', label: 'Offers' },
    { id: 'requests', label: 'Requests' },
    { id: 'your-listings', label: 'Your listings' },
  ];

  // Negotiations preview state
  const [negotiationsTab, setNegotiationsTab] = useState<string>('all');
  const negotiationsTabs: Tab[] = [
    { id: 'all', label: 'All negotiations' },
    { id: 'your-offers', label: 'Your offers' },
    { id: 'your-requests', label: 'Your requests' },
  ];

  // Filter marketplace data based on active tab
  const getFilteredMarketplaceData = (): MarketplaceProduct[] => {
    switch (marketplaceTab) {
      case 'offers':
        return mockMarketplaceData.filter(item => item.type === 'offer');
      case 'requests':
        return mockMarketplaceData.filter(item => item.type === 'request');
      case 'your-listings':
        return mockMarketplaceData.filter(item => item.type === 'listing');
      default:
        return mockMarketplaceData;
    }
  };

  // Filter negotiations data based on active tab
  const getFilteredNegotiationsData = (): Negotiation[] => {
    switch (negotiationsTab) {
      case 'your-offers':
        return mockNegotiationsData.filter(item => item.type === 'offer');
      case 'your-requests':
        return mockNegotiationsData.filter(item => item.type === 'request');
      default:
        return mockNegotiationsData;
    }
  };

  // Marketplace table columns
  const marketplaceColumns: Column<MarketplaceProduct>[] = [
    { id: 'product', label: 'Product' },
    { id: 'quantity', label: 'Quantity (MT)' },
    { id: 'pricePerUnit', label: 'Price/Unit' },
    { id: 'total', label: 'Total' },
    {
      id: 'view',
      label: '',
      render: () => (
        <button className="px-4 py-2 text-sm font-medium text-primary hover:text-primary-dark border border-primary rounded transition-colors">
          View
        </button>
      ),
    },
  ];

  // Negotiations table columns
  const negotiationsColumns: Column<Negotiation>[] = [
    { id: 'product', label: 'Product' },
    { id: 'quantity', label: 'Quantity' },
    { id: 'price', label: 'Price' },
    { id: 'total', label: 'Total' },
    {
      id: 'view',
      label: '',
      render: () => (
        <button className="px-4 py-2 text-sm font-medium text-primary hover:text-primary-dark border border-primary rounded transition-colors">
          View
        </button>
      ),
    },
  ];

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format number with unit
  const formatQuantity = (quantity: number): string => {
    return `${quantity} MT`;
  };

  // Tutorial card click handlers (mocked navigation)
  const handleTutorialClick = (route: string) => {
    // For MVP, these routes are mocked - they would navigate to help/tutorial pages
    console.log(`Navigate to ${route}`);
    // In future: navigate(`/help/${route}`);
  };

  return (
    <div className="min-h-screen bg-background w-full">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-h1 font-bold text-text-primary mb-2">
          Welcome, {user?.name || user?.email || 'User'}
        </h1>
        <p className="text-base text-text-secondary">
          Your trading dashboard and activity overview
        </p>
      </div>

      {/* Tutorial Cards Section */}
      <section className="mb-12">
        <h2 className="text-h2 font-semibold text-text-primary mb-6">
          Get Started
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title="Learn Basics"
            icon={<BookOpen className="w-6 h-6" />}
            onClick={() => handleTutorialClick('basics')}
          />
          <StatCard
            title="Products"
            icon={<Package className="w-6 h-6" />}
            onClick={() => handleTutorialClick('products')}
          />
          <StatCard
            title="Offers"
            icon={<Tag className="w-6 h-6" />}
            onClick={() => handleTutorialClick('offers')}
          />
        </div>
      </section>

      {/* Marketplace Block */}
      <section className="mb-12">
        <div className="bg-background border border-border rounded-lg overflow-hidden">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-h2 font-semibold text-text-primary">
                Marketplace
              </h2>
              <button
                onClick={() => navigate('/marketplace')}
                className="flex items-center gap-2 text-primary hover:text-primary-dark transition-colors font-medium"
              >
                View all
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            <Tabs
              tabs={marketplaceTabs}
              activeTab={marketplaceTab}
              onTabChange={setMarketplaceTab}
            />
          </div>
          <div className="p-6">
            <DataTable
              columns={marketplaceColumns.map(col => {
                if (col.id === 'pricePerUnit' || col.id === 'total') {
                  return {
                    ...col,
                    render: (row) => formatCurrency(row[col.id]),
                  };
                }
                if (col.id === 'quantity') {
                  return {
                    ...col,
                    render: (row) => formatQuantity(row[col.id]),
                  };
                }
                return col;
              })}
              data={getFilteredMarketplaceData()}
            />
          </div>
        </div>
      </section>

      {/* Negotiations Block */}
      <section className="mb-12">
        <div className="bg-background border border-border rounded-lg overflow-hidden">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-h2 font-semibold text-text-primary">
                Negotiations
              </h2>
              <button
                onClick={() => navigate('/negotiations')}
                className="flex items-center gap-2 text-primary hover:text-primary-dark transition-colors font-medium"
              >
                View all
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            <Tabs
              tabs={negotiationsTabs}
              activeTab={negotiationsTab}
              onTabChange={setNegotiationsTab}
            />
          </div>
          <div className="p-6">
            <DataTable
              columns={negotiationsColumns.map(col => {
                if (col.id === 'price' || col.id === 'total') {
                  return {
                    ...col,
                    render: (row) => formatCurrency(row[col.id]),
                  };
                }
                if (col.id === 'quantity') {
                  return {
                    ...col,
                    render: (row) => formatQuantity(row[col.id]),
                  };
                }
                return col;
              })}
              data={getFilteredNegotiationsData()}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

